import { useQuery } from '@tanstack/react-query';
import type {
  AgentConfigDTO,
  AuditEventDTO,
  CheckpointDTO,
  ConversationDTO,
  FeatureRunDTO,
  IntegrationsDTO,
  JiraIssueDTO,
  MemoryFactDTO,
  MemoryProposalDTO,
  ModelDTO,
  ProjectDTO,
  ProviderDTO,
  ServerConnectionDTO,
  SessionState,
  ConnectionsDTO,
  ProjectEnvironmentResponse,
} from '@harness/shared';
import { api } from './api';
import type { ConversationData } from './events';
import { qk } from './keys';

export type ProjectDetail = ProjectDTO & { uiState: Record<string, unknown> };

export const useSession = () => useQuery({ queryKey: qk.session, queryFn: () => api.get<SessionState>('/api/auth/session'), staleTime: 60_000 });

export const useProjects = () => useQuery({ queryKey: qk.projects, queryFn: () => api.get<ProjectDTO[]>('/api/projects'), staleTime: 15_000 });

/** Project by slug, resolved from the list first so switching is instant. */
export function useProject(slug: string | undefined) {
  const projects = useProjects();
  const fromList = projects.data?.find((p) => p.slug === slug);
  const detail = useQuery({
    queryKey: qk.project(fromList?.id ?? slug ?? ''),
    queryFn: () => api.get<ProjectDetail>(`/api/projects/${fromList?.id ?? slug}`),
    enabled: !!slug,
    staleTime: 15_000,
  });
  return { project: (fromList ? { ...detail.data, ...fromList, uiState: detail.data?.uiState ?? {} } : detail.data) as ProjectDetail | undefined, isLoading: projects.isLoading || (!fromList && detail.isLoading), error: detail.error };
}

export const useConversations = (projectId: string | undefined) =>
  useQuery({ queryKey: qk.conversations(projectId ?? ''), queryFn: () => api.get<ConversationDTO[]>(`/api/projects/${projectId}/conversations`), enabled: !!projectId, staleTime: 30_000 });

export const useConversation = (id: string | undefined) =>
  useQuery({ queryKey: qk.conversation(id ?? ''), queryFn: () => api.get<ConversationData>(`/api/conversations/${id}`), enabled: !!id, staleTime: 60_000 });

export const useAgents = (projectId: string | undefined) =>
  useQuery({ queryKey: qk.agents(projectId ?? ''), queryFn: () => api.get<AgentConfigDTO[]>(`/api/projects/${projectId}/agents`), enabled: !!projectId });

export const useModels = () => useQuery({ queryKey: qk.models, queryFn: () => api.get<ModelDTO[]>('/api/models'), staleTime: 60_000 });

export const useProviders = () => useQuery({ queryKey: qk.providers, queryFn: () => api.get<ProviderDTO[]>('/api/providers') });

export const useMemory = (projectId: string | undefined) =>
  useQuery({
    queryKey: qk.memory(projectId ?? ''),
    queryFn: () => api.get<{ facts: MemoryFactDTO[]; proposals: MemoryProposalDTO[] }>(`/api/projects/${projectId}/memory`),
    enabled: !!projectId,
  });

export const useServer = (projectId: string | undefined) =>
  useQuery({
    queryKey: qk.server(projectId ?? ''),
    queryFn: () => api.get<{ connection: ServerConnectionDTO | null; lastError: string | null; localRoot: string; daytonaAvailable: boolean }>(`/api/projects/${projectId}/server`),
    enabled: !!projectId,
  });

export const useEnvironment = (projectId: string | undefined) =>
  useQuery({ queryKey: qk.environment(projectId ?? ''), queryFn: () => api.get<ProjectEnvironmentResponse>(`/api/projects/${projectId}/environment`), enabled: !!projectId, refetchInterval: 10_000 });

export const useAudit = (projectId: string | undefined, limit = 100) =>
  useQuery({ queryKey: [...qk.audit(projectId ?? ''), limit], queryFn: () => api.get<AuditEventDTO[]>(`/api/projects/${projectId}/audit?limit=${limit}`), enabled: !!projectId });

export const useCheckpoints = (projectId: string | undefined) =>
  useQuery({ queryKey: qk.checkpoints(projectId ?? ''), queryFn: () => api.get<CheckpointDTO[]>(`/api/projects/${projectId}/checkpoints`), enabled: !!projectId });

/** Your own GitHub / Jira sign-ins (Settings › Connections). */
export const useConnections = () => useQuery({ queryKey: ['connections'], queryFn: () => api.get<ConnectionsDTO>('/api/connections'), staleTime: 60_000 });

export const useIntegrations = (projectId: string | undefined) =>
  useQuery({
    queryKey: qk.integrations(projectId ?? ''),
    queryFn: () => api.get<IntegrationsDTO>(`/api/projects/${projectId}/integrations`),
    enabled: !!projectId,
    retry: false,
  });

export const useIssues = (projectId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: qk.issues(projectId ?? ''),
    queryFn: () => api.get<JiraIssueDTO[]>(`/api/projects/${projectId}/jira/issues`),
    enabled: !!projectId && enabled,
    refetchInterval: 5 * 60_000,
    retry: false,
  });

export const useFeatures = (projectId: string | undefined) =>
  useQuery({ queryKey: qk.features(projectId ?? ''), queryFn: () => api.get<FeatureRunDTO[]>(`/api/projects/${projectId}/features`), enabled: !!projectId, retry: false });

export const useFeature = (id: string | null | undefined) =>
  useQuery({ queryKey: qk.feature(id ?? ''), queryFn: () => api.get<FeatureRunDTO>(`/api/features/${id}`), enabled: !!id, retry: false });
